/**
 * 灵感词业务逻辑服务（Class + 静态方法）
 *
 * @module utils/service/inspirationWords
 */

import {
  getInspirationRecommendWords,
  UserBehaviorType,
} from "@/services/api/inspirationWords";
import {
  OperationType,
  type OperationRecord,
  type TriggerConfig,
  type TriggerResult,
  type WordMetadata,
} from "@/types/inspirationWords";
import { sortBy } from "lodash";

/**
 * 灵感词服务类（静态方法）
 *
 * 设计理念：
 * 1. 使用 Class 提供命名空间
 * 2. 所有方法都是静态方法（无需实例化）
 * 3. 保持纯函数特性（无副作用）
 * 4. 封装常量配置
 */
export class InspirationWordsService {
  /** 默认触发配置 */
  static readonly DEFAULT_CONFIG: TriggerConfig = {
    twoMinutesCount: 2,
    threeMinutesCount: 3,
    cooldownMinutes: 5,
  };

  /** 推荐词最大数量 */
  static readonly MAX_WORDS = 5;

  /** 记录最大保留时间（5分钟） */
  static readonly MAX_AGE = 5 * 60 * 1000;

  /** Mock 灵感词库 */
  private static readonly MOCK_WORDS = [
    "红色背景",
    "蓝色背景",
    "绿色背景",
    "简约风格",
    "现代设计",
    "插画设计",
    "扁平化设计",
    "渐变背景",
    "几何图形",
    "抽象艺术",
    "自然风景",
    "城市建筑",
    "人物肖像",
    "美食摄影",
    "旅行风景",
    "手绘插画",
    "水彩画",
    "油画风格",
    "黑白摄影",
    "复古风格",
    "科技感",
    "未来感",
    "极简主义",
    "工业风",
    "日式风格",
    "北欧风格",
    "中国风",
    "卡通风格",
    "3D渲染",
    "纹理背景",
  ];

  /**
   * Mock 函数：随机返回灵感词
   * @param count 返回数量，默认 5-15 个
   * @returns 随机选择的推荐词数组
   */
  private static getMockRecommendWords(count?: number): Array<{
    recommend_word: string;
    ext?: Record<string, any>;
  }> {
    const wordCount = count || Math.floor(Math.random() * 11) + 5; // 5-15 个
    const shuffled = [...this.MOCK_WORDS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, wordCount).map((word) => ({
      recommend_word: word,
      ext: {},
    }));
  }

  /**
   * OperationType 到 UserBehaviorType 的映射
   */
  private static mapOperationTypeToUserBehaviorType(
    type: OperationType
  ): UserBehaviorType {
    switch (type) {
      case OperationType.COLLECT:
        return UserBehaviorType.COLLECT;
      case OperationType.CLICK:
      case OperationType.RIGHT_CLICK:
        return UserBehaviorType.CLICK;
      case OperationType.DOWNLOAD:
        return UserBehaviorType.DOWNLOAD;
      default:
        return UserBehaviorType.CLICK;
    }
  }

  /**
   * 获取推荐词（包含元数据）
   * @param fileId 文件ID（内容的唯一标识）
   * @param type 操作类型
   * @returns 推荐词元数据列表
   */
  static async fetchRecommendWords(
    fileId: string,
    type: OperationType
  ): Promise<WordMetadata[]> {
    try {
      // 映射操作类型到用户行为类型
      const userBehaviorType = this.mapOperationTypeToUserBehaviorType(type);

      // TODO: 接口准备好后，取消注释下面这行，删除 mock 代码
      const data = await getInspirationRecommendWords(fileId, userBehaviorType);

      // Mock 数据：随机返回灵感词（模拟接口延迟）
      // await new Promise((resolve) =>
      //   setTimeout(resolve, 100 + Math.random() * 200)
      // );
      // const data = this.getMockRecommendWords(15);

      // 接口返回数组，提取 recommend_word 和 ext.dist_code
      if (Array.isArray(data)) {
        return data
          .map((item: any) => {
            const word = item?.recommend_word;
            // 从 ext 中提取 dist_code，如果没有则使用默认值
            const distCode = item?.ext?.dist_code || "unknown";

            if (!word) return null;

            return {
              word,
              distCode,
            };
          })
          .filter((item): item is WordMetadata => item !== null);
      }

      return [];
    } catch (error) {
      console.error(
        "[InspirationWordsService] fetchRecommendWords error:",
        error
      );
      return [];
    }
  }

  /**
   * 计算共现词次数
   * @param currentWords 当前内容的推荐词元数据
   * @param historyRecords 历史操作记录
   * @returns 共现次数 Map（key 为词文本）
   */
  static calculateCooccurrence(
    currentWords: WordMetadata[],
    historyRecords: OperationRecord[]
  ): Map<string, number> {
    const map = new Map<string, number>();

    // 初始化当前词的计数为 1
    currentWords.forEach((wordMeta) => map.set(wordMeta.word, 1));

    // 提取当前词文本用于快速查找
    const currentWordTexts = currentWords.map((w) => w.word);

    // 遍历历史记录，累加共现次数
    historyRecords.forEach((record) => {
      record.recommendWords?.forEach((wordMeta) => {
        if (currentWordTexts.includes(wordMeta.word)) {
          map.set(wordMeta.word, (map.get(wordMeta.word) || 0) + 1);
        }
      });
    });

    return map;
  }

  /**
   * 排序推荐词
   * 规则：
   * 1. 按共现次数降序
   * 2. 相同次数保持原顺序
   * 3. 取前 5 个
   *
   * @param words 推荐词元数据列表（已排序）
   * @param cooccurrenceMap 共现次数 Map
   * @returns 排序后的推荐词元数据（最多5个）
   */
  static sortWords(
    words: WordMetadata[],
    cooccurrenceMap: Map<string, number>
  ): WordMetadata[] {
    const wordsWithMeta = words.map((wordMeta, index) => ({
      wordMeta,
      count: cooccurrenceMap.get(wordMeta.word) || 0,
      originalIndex: index,
    }));

    // 使用 lodash sortBy 进行稳定排序
    const sorted = sortBy(wordsWithMeta, [
      (item) => -item.count, // 共现次数降序
      (item) => item.originalIndex, // 原顺序升序
    ]);

    return sorted.slice(0, this.MAX_WORDS).map((item) => item.wordMeta);
  }

  /**
   * 检查触发条件
   * @param records 操作记录列表
   * @param config 触发配置
   * @param lastTriggerTime 上次触发时间
   * @returns 触发结果
   */
  static checkTrigger(
    records: OperationRecord[],
    config: TriggerConfig,
    lastTriggerTime: number
  ): TriggerResult {
    const now = Date.now();

    // 检查冷却时间
    if (now - lastTriggerTime < config.cooldownMinutes * 60 * 1000) {
      return { triggered: false };
    }

    if (records.length === 0) {
      return { triggered: false };
    }

    // 检查 2 分钟内 2 个内容（条件1：需要存在共现词）
    const twoMinutesRecords = records.filter(
      (r) => now - r.timestamp <= 2 * 60 * 1000
    );

    if (twoMinutesRecords.length >= config.twoMinutesCount) {
      // 条件1：需要检查是否存在共现词
      const latestRecord = records[records.length - 1];
      const historyRecords = twoMinutesRecords
        .slice(0, -1)
        .filter((r) => r.recommendWords && r.recommendWords.length > 0);

      // 计算共现词
      const cooccurrenceMap = this.calculateCooccurrence(
        latestRecord.recommendWords || [],
        historyRecords
      );

      // 检查是否存在共现次数大于1的词
      const hasCooccurrence = Array.from(cooccurrenceMap.values()).some(
        (count) => count > 1
      );

      // 只有存在共现词时才触发（条件1的要求）
      if (hasCooccurrence) {
        return this.processTriggered(
          records,
          twoMinutesRecords,
          "共现触发",
          cooccurrenceMap
        );
      }
      // 如果没有共现词，不触发（条件1不满足）
    }

    // 检查 3 分钟内 3 个内容（条件2：即使无共现词也展示）
    const threeMinutesRecords = records.filter(
      (r) => now - r.timestamp <= 3 * 60 * 1000
    );

    if (threeMinutesRecords.length >= config.threeMinutesCount) {
      return this.processTriggered(records, threeMinutesRecords, "累计触发");
    }

    return { triggered: false };
  }

  /**
   * 处理触发逻辑（私有方法）
   * @param records 所有操作记录
   * @param timeRangeRecords 时间范围内的记录
   * @param triggerType 触发类型
   * @param cooccurrenceMap 可选的共现词 Map（如果已计算过，避免重复计算）
   * @returns 触发结果
   */
  private static processTriggered(
    records: OperationRecord[],
    timeRangeRecords: OperationRecord[],
    triggerType: "共现触发" | "累计触发",
    cooccurrenceMap?: Map<string, number>
  ): TriggerResult {
    const latestRecord = records[records.length - 1];
    const historyRecords = timeRangeRecords
      .slice(0, -1)
      .filter((r) => r.recommendWords && r.recommendWords.length > 0);

    // 如果没有传入共现词 Map，则计算
    if (!cooccurrenceMap) {
      cooccurrenceMap = this.calculateCooccurrence(
        latestRecord.recommendWords || [],
        historyRecords
      );
    }

    // 排序推荐词
    const sortedWords = this.sortWords(
      latestRecord.recommendWords || [],
      cooccurrenceMap
    );

    // 计算来源 file_ids（触发时间范围内的所有 file_ids，逗号分隔）
    const sourceFileIds = timeRangeRecords
      .map((r) => r.fileId)
      .filter(Boolean)
      .join(",");

    return {
      triggered: true,
      fileId: latestRecord.fileId,
      words: sortedWords,
      triggerType,
      sourceFileIds,
    };
  }

  /**
   * 过滤过期记录
   * @param records 操作记录列表
   * @param maxAge 最大保留时间（毫秒），默认 5 分钟
   * @returns 过滤后的记录
   */
  static filterExpired(
    records: OperationRecord[],
    maxAge: number = this.MAX_AGE
  ): OperationRecord[] {
    const now = Date.now();
    return records.filter((r) => now - r.timestamp <= maxAge);
  }
}
